import type { CSSProperties, ReactNode } from 'react';

import { STR } from '../../../../i18n';
import { monoStyle } from '../../../../ui/styles';

import { IT_TONE, type ItTone, toneLines } from './tone';

/** "Set up" / "Remove" in its tone. The word is what carries the meaning; the colour adds. */
export function ToneWord({ tone, style }: { tone: ItTone; style?: CSSProperties }) {
  return (
    <span className={IT_TONE[tone].className} style={{ fontSize: 11.5, fontWeight: 700, ...style }}>
      {IT_TONE[tone].word}
    </span>
  );
}

function EdgeSwatch({ tone }: { tone: ItTone }) {
  return (
    <span
      aria-hidden="true"
      style={{ width: 3, height: 14, borderRadius: 2, background: IT_TONE[tone].bar, flex: 'none' }}
    />
  );
}

/** The two tones and what each asks IT to do — shown wherever both can appear. */
export function ToneLegend() {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
      {(['setup', 'remove'] as const).map((tone) => (
        <span key={tone} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <EdgeSwatch tone={tone} />
          <ToneWord tone={tone} style={{ fontSize: 12 }} />
          <span className="tx-subtle">
            {tone === 'setup' ? STR.itEmails.legendSetUp : STR.itEmails.legendRemove}
          </span>
        </span>
      ))}
    </div>
  );
}

/** "2 set up · 1 remove", each half in its tone; a zero half is left out. */
export function ToneCounts({ setUp, remove }: { setUp: number; remove: number }) {
  const parts: ReactNode[] = [];
  if (setUp > 0) {
    parts.push(
      <span key="s" className={IT_TONE.setup.className} style={{ fontWeight: 650 }}>
        {STR.itEmails.countSetUp(setUp)}
      </span>,
    );
  }
  if (remove > 0) {
    if (parts.length > 0) {
      parts.push(
        <span key="d" className="tx-subtle">
          {STR.itEmails.countSeparator}
        </span>,
      );
    }
    parts.push(
      <span key="r" className={IT_TONE.remove.className} style={{ fontWeight: 650 }}>
        {STR.itEmails.countRemove(remove)}
      </span>,
    );
  }
  return <span style={{ fontSize: 11.5 }}>{parts}</span>;
}

const SET_UP_WORD = 'SET UP.';
const REMOVE_WORD = 'REMOVE.';

/** One line, with its SET UP. / REMOVE. word in bold when the line carries a tone. */
function lineBody(text: string, tone: ItTone | null): ReactNode {
  if (tone === null) return text === '' ? ' ' : text;
  const word = text.includes(SET_UP_WORD) ? SET_UP_WORD : text.includes(REMOVE_WORD) ? REMOVE_WORD : null;
  if (word === null) return text;
  const at = text.indexOf(word);
  return (
    <>
      {text.slice(0, at)}
      <b>{word}</b>
      {text.slice(at + word.length)}
    </>
  );
}

/**
 * THE EMAIL IN COLOUR — the read view of an IT email's stored text. Each SET UP / REMOVE
 * heading and its bullets get the tone's text colour and edge bar; every other line is plain.
 * The text itself is shown exactly as stored (and is what Copy text copies): only the
 * colouring is added. Edit text swaps this for the plain box.
 */
export function ColouredEmail({ text, maxHeight }: { text: string; maxHeight?: number | 'none' }) {
  return (
    <pre
      style={{
        ...monoStyle,
        margin: 0,
        padding: '10px 12px 10px 4px',
        borderRadius: 10,
        border: '1px solid var(--border)',
        background: 'var(--surface-2)',
        fontSize: 12,
        lineHeight: 1.55,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        overflowY: 'auto',
        maxHeight: maxHeight ?? 420,
      }}
    >
      {toneLines(text).map((line, index) => (
        <span
          // Lines are positional: the text is shown read-only, so the index is stable.
          key={index}
          className={line.tone === null ? undefined : IT_TONE[line.tone].className}
          style={{
            display: 'block',
            paddingLeft: 10,
            ...(line.tone === null ? {} : { boxShadow: `inset 3px 0 0 ${IT_TONE[line.tone].bar}` }),
          }}
        >
          {lineBody(line.text, line.tone)}
        </span>
      ))}
    </pre>
  );
}
